import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  uuid,
  timestamp,
  numeric,
  integer,
  date,
  boolean,
  jsonb,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================================================
// COMMISSION MANAGER (MODULE 08) - 6 existing tables + rcti_line_items
// Commission import, matching, calculations, RCTI, partner payments, trail
// Sprint 20
// ============================================================================

// ============================================================================
// 1. COMMISSION TRANSACTIONS TABLE
// Raw imported commission transactions from lender statements
// ============================================================================

export const commissionTransactions = pgTable("crm_x_commission_transactions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Import tracking
  importBatchId: uuid("import_batch_id"),
  importDate: timestamp("import_date", { withTimezone: true }).defaultNow(),
  sourceFile: varchar("source_file", { length: 255 }),
  sourceRowNumber: integer("source_row_number"),

  // Transaction details
  transactionDate: date("transaction_date").notNull(),
  transactionReference: varchar("transaction_reference", { length: 100 }),
  paymentDate: date("payment_date"),

  // Lender info (raw from file)
  lenderAccountNumber: varchar("lender_account_number", { length: 100 }).notNull(),
  lenderName: varchar("lender_name", { length: 255 }).notNull(),
  lenderId: uuid("lender_id"),

  // Client matching
  clientNameFromFile: varchar("client_name_from_file", { length: 255 }),
  matchedClientGroupId: uuid("matched_client_group_id"),
  matchedContactId: uuid("matched_contact_id"),
  matchedPartnerId: uuid("matched_partner_id"),
  matchConfidence: numeric("match_confidence", { precision: 5, scale: 2 }),
  matchMethod: varchar("match_method", { length: 50 }),
  matchedAt: timestamp("matched_at", { withTimezone: true }),
  matchedBy: uuid("matched_by"),

  // Deal/facility linking
  dealId: uuid("deal_id"),
  facilityId: uuid("facility_id"),
  commissionCalculationId: uuid("commission_calculation_id"),

  // Financial
  commissionType: varchar("commission_type", { length: 20 }).notNull(),
  commissionAmount: numeric("commission_amount", { precision: 12, scale: 2 }).notNull(),
  gstAmount: numeric("gst_amount", { precision: 12, scale: 2 }),
  totalAmount: numeric("total_amount", { precision: 12, scale: 2 }).notNull(),
  loanAmount: numeric("loan_amount", { precision: 15, scale: 2 }),
  loanType: varchar("loan_type", { length: 100 }),

  // Processing
  description: text("description"),
  processingStatus: varchar("processing_status", { length: 20 }).default("pending"),
  requiresReview: boolean("requires_review").default(false),
  reviewNotes: text("review_notes"),
  isAdjustment: boolean("is_adjustment").default(false),
  isClawback: boolean("is_clawback").default(false),
  originalTransactionId: uuid("original_transaction_id"),

  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// ============================================================================
// 2. COMMISSION CALCULATIONS TABLE
// Calculated expected commissions per deal/facility
// ============================================================================

export const commissionCalculations = pgTable("crm_x_commission_calculations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").notNull(),
  facilityId: uuid("facility_id"),
  lenderId: uuid("lender_id"),
  lenderCommissionId: uuid("lender_commission_id"),

  commissionType: varchar("commission_type", { length: 20 }).notNull(),
  settledAmount: numeric("settled_amount", { precision: 15, scale: 2 }),
  commissionRate: numeric("commission_rate", { precision: 5, scale: 4 }),
  grossCommission: numeric("gross_commission", { precision: 12, scale: 2 }),
  gstAmount: numeric("gst_amount", { precision: 12, scale: 2 }).default("0"),
  netCommission: numeric("net_commission", { precision: 12, scale: 2 }),
  commissionStatus: varchar("commission_status", { length: 20 }).default("expected"),

  // Dates
  expectedDate: date("expected_date"),
  confirmedDate: date("confirmed_date"),
  invoicedDate: date("invoiced_date"),
  receivedDate: date("received_date"),

  // Xero integration
  xeroInvoiceId: varchar("xero_invoice_id", { length: 100 }),
  xeroInvoiceNumber: varchar("xero_invoice_number", { length: 50 }),

  notes: text("notes"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// 3. PARTNER PAYMENTS TABLE
// Payments made to referral partners (RCTI documents)
// ============================================================================

export const partnerPayments = pgTable("crm_x_partner_payments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: uuid("partner_id").notNull(),

  // Payment details
  paymentReference: varchar("payment_reference", { length: 50 }),
  paymentDate: date("payment_date"),
  paymentMethod: varchar("payment_method", { length: 50 }),

  // Financial
  grossAmount: numeric("gross_amount", { precision: 12, scale: 2 }).notNull(),
  gstAmount: numeric("gst_amount", { precision: 12, scale: 2 }).default("0"),
  netAmount: numeric("net_amount", { precision: 12, scale: 2 }).notNull(),
  paymentStatus: varchar("payment_status", { length: 20 }).default("pending"),

  // Banking
  bankName: varchar("bank_name", { length: 100 }),
  bsb: varchar("bsb", { length: 7 }),
  accountNumber: varchar("account_number", { length: 20 }),
  accountName: varchar("account_name", { length: 255 }),

  // Xero integration
  xeroBillId: varchar("xero_bill_id", { length: 100 }),
  xeroPaymentId: varchar("xero_payment_id", { length: 100 }),

  // Approval / processing
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  approvedBy: uuid("approved_by"),
  processedAt: timestamp("processed_at", { withTimezone: true }),
  processedBy: uuid("processed_by"),

  notes: text("notes"),
  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),

  // RCTI columns (added via Sprint 20 migration)
  paymentMonth: integer("payment_month"),
  paymentYear: integer("payment_year"),
  internalReference: varchar("internal_reference", { length: 100 }),
  totalLineItems: integer("total_line_items"),
  totalCommission: numeric("total_commission", { precision: 12, scale: 2 }),
  totalGst: numeric("total_gst", { precision: 12, scale: 2 }),
  totalPaymentAmount: numeric("total_payment_amount", { precision: 12, scale: 2 }),
  xeroRctiNumber: varchar("xero_rcti_number", { length: 50 }),
  xeroGeneratedDate: date("xero_generated_date"),
  rctiSentDate: date("rcti_sent_date"),
  paymentReceivedDate: date("payment_received_date"),
});

// ============================================================================
// 4. PARTNER PAYMENT LINES TABLE
// Line items linking a partner payment to commission splits
// ============================================================================

export const partnerPaymentLines = pgTable("crm_x_partner_payment_lines", {
  paymentId: uuid("payment_id").notNull(),
  commissionSplitId: uuid("commission_split_id").notNull(),
  amount: numeric("amount", { precision: 12, scale: 2 }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// 5. PARTNER COMMISSION SPLITS TABLE
// Breakdown of commission splits per partner per calculation
// ============================================================================

export const partnerCommissionSplits = pgTable("crm_x_partner_commission_splits", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  commissionCalculationId: uuid("commission_calculation_id").notNull(),
  partnerId: uuid("partner_id").notNull(),
  referralId: uuid("referral_id"),

  splitPercentage: numeric("split_percentage", { precision: 5, scale: 2 }),
  grossAmount: numeric("gross_amount", { precision: 12, scale: 2 }),
  gstAmount: numeric("gst_amount", { precision: 12, scale: 2 }).default("0"),
  netAmount: numeric("net_amount", { precision: 12, scale: 2 }),
  splitStatus: varchar("split_status", { length: 20 }).default("pending"),

  approvedAt: timestamp("approved_at", { withTimezone: true }),
  approvedBy: uuid("approved_by"),

  notes: text("notes"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// 6. TRAIL COMMISSIONS TABLE
// Monthly trail commission income from existing loan book
// ============================================================================

export const trailCommissions = pgTable("crm_x_trail_commissions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").notNull(),
  facilityId: uuid("facility_id"),
  lenderId: uuid("lender_id"),

  periodStartDate: date("period_start_date").notNull(),
  periodEndDate: date("period_end_date").notNull(),

  outstandingBalance: numeric("outstanding_balance", { precision: 15, scale: 2 }),
  trailRate: numeric("trail_rate", { precision: 5, scale: 4 }),
  grossTrail: numeric("gross_trail", { precision: 12, scale: 2 }),
  gstAmount: numeric("gst_amount", { precision: 12, scale: 2 }).default("0"),
  netTrail: numeric("net_trail", { precision: 12, scale: 2 }),
  trailStatus: varchar("trail_status", { length: 20 }).default("expected"),
  receivedDate: date("received_date"),

  notes: text("notes"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// 7. RCTI LINE ITEMS TABLE (new — Sprint 20 migration)
// Detailed line items for each RCTI partner payment document
// ============================================================================

export const rctiLineItems = pgTable("crm_x_rcti_line_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  paymentId: uuid("payment_id").notNull(),
  accountNumber: varchar("account_number", { length: 100 }),
  dealReference: varchar("deal_reference", { length: 100 }),
  commissionType: varchar("commission_type", { length: 20 }),
  commissionAmount: numeric("commission_amount", { precision: 12, scale: 2 }).notNull(),
  gstAmount: numeric("gst_amount", { precision: 12, scale: 2 }).default("0"),
  totalAmount: numeric("total_amount", { precision: 12, scale: 2 }).notNull(),
  sourceTransactionIds: jsonb("source_transaction_ids").default(sql`'[]'::jsonb`),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS — Commission Transactions
// ============================================================================

export const insertCommissionTransactionSchema = createInsertSchema(commissionTransactions, {
  transactionDate: z.string().min(1, "Transaction date is required"),
  lenderAccountNumber: z.string().min(1, "Lender account number is required"),
  lenderName: z.string().min(1, "Lender name is required"),
  commissionType: z.string().min(1, "Commission type is required"),
  commissionAmount: z.string().min(1, "Commission amount is required"),
  totalAmount: z.string().min(1, "Total amount is required"),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateCommissionTransactionSchema = insertCommissionTransactionSchema.partial();
export const selectCommissionTransactionSchema = createSelectSchema(commissionTransactions);

// ============================================================================
// ZOD SCHEMAS — Commission Calculations
// ============================================================================

export const insertCommissionCalculationSchema = createInsertSchema(commissionCalculations, {
  dealId: z.string().uuid("Deal ID must be a valid UUID"),
  commissionType: z.string().min(1, "Commission type is required"),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
});

export const updateCommissionCalculationSchema = insertCommissionCalculationSchema.partial();
export const selectCommissionCalculationSchema = createSelectSchema(commissionCalculations);

// ============================================================================
// ZOD SCHEMAS — Partner Payments
// ============================================================================

export const insertPartnerPaymentSchema = createInsertSchema(partnerPayments, {
  partnerId: z.string().uuid("Partner ID must be a valid UUID"),
  grossAmount: z.string().min(1, "Gross amount is required"),
  netAmount: z.string().min(1, "Net amount is required"),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
});

export const updatePartnerPaymentSchema = insertPartnerPaymentSchema.partial();
export const selectPartnerPaymentSchema = createSelectSchema(partnerPayments);

// ============================================================================
// ZOD SCHEMAS — Partner Payment Lines
// ============================================================================

export const insertPartnerPaymentLineSchema = createInsertSchema(partnerPaymentLines, {
  paymentId: z.string().uuid("Payment ID must be a valid UUID"),
  commissionSplitId: z.string().uuid("Commission split ID must be a valid UUID"),
  amount: z.string().min(1, "Amount is required"),
}).omit({
  createdAt: true,
});

export const updatePartnerPaymentLineSchema = insertPartnerPaymentLineSchema.partial();
export const selectPartnerPaymentLineSchema = createSelectSchema(partnerPaymentLines);

// ============================================================================
// ZOD SCHEMAS — Partner Commission Splits
// ============================================================================

export const insertPartnerCommissionSplitSchema = createInsertSchema(partnerCommissionSplits, {
  commissionCalculationId: z.string().uuid("Commission calculation ID must be a valid UUID"),
  partnerId: z.string().uuid("Partner ID must be a valid UUID"),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updatePartnerCommissionSplitSchema = insertPartnerCommissionSplitSchema.partial();
export const selectPartnerCommissionSplitSchema = createSelectSchema(partnerCommissionSplits);

// ============================================================================
// ZOD SCHEMAS — Trail Commissions
// ============================================================================

export const insertTrailCommissionSchema = createInsertSchema(trailCommissions, {
  dealId: z.string().uuid("Deal ID must be a valid UUID"),
  periodStartDate: z.string().min(1, "Period start date is required"),
  periodEndDate: z.string().min(1, "Period end date is required"),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateTrailCommissionSchema = insertTrailCommissionSchema.partial();
export const selectTrailCommissionSchema = createSelectSchema(trailCommissions);

// ============================================================================
// ZOD SCHEMAS — RCTI Line Items
// ============================================================================

export const insertRctiLineItemSchema = createInsertSchema(rctiLineItems, {
  paymentId: z.string().uuid("Payment ID must be a valid UUID"),
  commissionAmount: z.string().min(1, "Commission amount is required"),
  totalAmount: z.string().min(1, "Total amount is required"),
}).omit({
  id: true,
  createdAt: true,
});

export const selectRctiLineItemSchema = createSelectSchema(rctiLineItems);

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type InsertCommissionTransaction = z.infer<typeof insertCommissionTransactionSchema>;
export type UpdateCommissionTransaction = z.infer<typeof updateCommissionTransactionSchema>;
export type CommissionTransaction = typeof commissionTransactions.$inferSelect;

export type InsertCommissionCalculation = z.infer<typeof insertCommissionCalculationSchema>;
export type UpdateCommissionCalculation = z.infer<typeof updateCommissionCalculationSchema>;
export type CommissionCalculation = typeof commissionCalculations.$inferSelect;

export type InsertPartnerPayment = z.infer<typeof insertPartnerPaymentSchema>;
export type UpdatePartnerPayment = z.infer<typeof updatePartnerPaymentSchema>;
export type PartnerPayment = typeof partnerPayments.$inferSelect;

export type InsertPartnerPaymentLine = z.infer<typeof insertPartnerPaymentLineSchema>;
export type UpdatePartnerPaymentLine = z.infer<typeof updatePartnerPaymentLineSchema>;
export type PartnerPaymentLine = typeof partnerPaymentLines.$inferSelect;

export type InsertPartnerCommissionSplit = z.infer<typeof insertPartnerCommissionSplitSchema>;
export type UpdatePartnerCommissionSplit = z.infer<typeof updatePartnerCommissionSplitSchema>;
export type PartnerCommissionSplit = typeof partnerCommissionSplits.$inferSelect;

export type InsertTrailCommission = z.infer<typeof insertTrailCommissionSchema>;
export type UpdateTrailCommission = z.infer<typeof updateTrailCommissionSchema>;
export type TrailCommission = typeof trailCommissions.$inferSelect;

export type InsertRctiLineItem = z.infer<typeof insertRctiLineItemSchema>;
export type RctiLineItem = typeof rctiLineItems.$inferSelect;
